import { useState, useEffect, useContext, type ChangeEvent } from "react";
import { useLocalization } from "@fluent/react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ContentCard,
  TextInput,
  Button,
  Row,
  MenuButton,
  Avatar,
  Chip,
  Tooltip,
  formatNumber,
  Select,
} from "@narmi/design_system";
import { useNotificationContext } from "cerulean";
import { useRolesContext } from "byzantine/src/Role/RolesProvider";
import { MODEL_STR, type Role } from "byzantine/src/Role/Role";
import type Account from "byzantine/src/Account";
import OrganizationUser from "byzantine/src/OrganizationUser";
import { groupAccountsByOrganization } from "byzantine";
import InstitutionSettingsContext from "../contexts/InstitutionSettingsContext";
import {
  splitRoles,
  filterRolesByName,
  DEFAULT_ROLE_NAMES,
  getAccountAccessForRole,
  getPermissionDescriptions,
  getExternalAccountSubtitle,
  translatePermissionFtlIds,
  LIMITS_DATA,
  hasWritePermissionForModels,
  hasAnyWritePermissionForLimits,
  getActiveUserCountForRole,
  getActiveUsersForRole,
} from "./OrganizationRoles.helpers";
import AccountTile from "./AccountTile";
import { AccountsGrid, OrgGroupedAccounts } from "./AccountsGrid";
import LongRoleTile from "./LongRoleTile";
import DeleteRoleModal from "./DeleteRoleModal";
import styles from "./OrganizationRoles.module.scss";

type OrganizationRolesProps = {
  organizationUuid?: UUID;
  organizationUsers?: OrganizationUser[];
  allAccounts?: (API.Account | API.ExternalAccount)[];
  onRefreshUsers?: () => void;
};

type RoleTileProps = {
  role: Role;
  selectedUuid?: string | null;
  organizationUsers?: OrganizationUser[];
  isDefault?: boolean;
  l10n: ReturnType<typeof useLocalization>["l10n"];
  onSelect: (roleUuid: string) => void;
};

function RoleTile({
  role,
  selectedUuid,
  organizationUsers,
  isDefault,
  l10n,
  onSelect,
}: RoleTileProps) {
  const handleClick = () => {
    if (role.uuid) {
      onSelect(role.uuid);
    }
  };

  const isSelected = role.uuid === selectedUuid;
  const userCount = getActiveUserCountForRole(role.uuid, organizationUsers);
  const userCountText = l10n.getString("org-role-user-count", {
    count: userCount,
  });

  return (
    <div
      onClick={handleClick}
      onKeyDown={(e) =>
        e.key === "Enter" || e.key === " " ? handleClick() : undefined
      }
      role="button"
      tabIndex={0}
      className={`${styles.roleTile} ${isSelected ? styles.selected : ""}`}
    >
      <div className={styles.roleName}>
        <Row gapSize="xs" alignItems="center">
          <div>{role.name}</div>
          {isDefault && (
            <Chip
              isLabelVisible={false}
              startIcon="lock"
              label={l10n.getString("org-role-locked")}
            />
          )}
        </Row>
      </div>
      <p className={styles.roleUserCount}>{userCountText}</p>
    </div>
  );
}

type ToggleAccountsButtonProps = {
  isExpanded: boolean;
  onToggle: () => void;
  expandedLabel: string;
  collapsedLabel: string;
};

function ToggleAccountsButton({
  isExpanded,
  onToggle,
  expandedLabel,
  collapsedLabel,
}: ToggleAccountsButtonProps) {
  return (
    <div style={{ marginTop: "16px" }}>
      <Button
        kind="plain"
        onClick={onToggle}
        endIcon={isExpanded ? "chevron-up" : "chevron-down"}
      >
        {isExpanded ? expandedLabel : collapsedLabel}
      </Button>
    </div>
  );
}

type RoleInfoProps = {
  role: Role | null;
  organizationUsers?: OrganizationUser[];
  allAccounts?: (API.Account | API.ExternalAccount)[];
  orgLimits?: API.Limits.ByType;
  hasInstitutionLoaded?: boolean;
  l10n: ReturnType<typeof useLocalization>["l10n"];
  assignableRoles: Role[];
  onRefreshUsers?: () => void;
  onNavigateToEditRole: (roleUuid: string) => void;
  onNavigateToDuplicateRole: (roleUuid: string) => void;
};

function RoleInfo({
  role,
  organizationUsers,
  allAccounts,
  orgLimits,
  hasInstitutionLoaded,
  l10n,
  assignableRoles,
  onRefreshUsers,
  onNavigateToEditRole,
  onNavigateToDuplicateRole,
}: RoleInfoProps) {
  const { sendNotification } = useNotificationContext();
  const { refresh } = useRolesContext();
  const institution = useContext(InstitutionSettingsContext);
  const [expandAccountList, setExpandAccountList] = useState(false);
  const [showAdminPermissions, setShowAdminPermissions] = useState(false);
  const [showAllPermissions, setShowAllPermissions] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Reset expanded state when role changes
  useEffect(() => {
    setExpandAccountList(false);
    setShowAdminPermissions(false);
    setShowAllPermissions(false);
    setIsDeleteModalOpen(false);
  }, [role?.uuid]);

  if (!role) {
    return (
      <ContentCard kind="bordered">
        <div className={styles.emptyStateMessage}>
          {l10n.getString("org-role-empty-state")}
        </div>
      </ContentCard>
    );
  }

  const usersForRole = getActiveUsersForRole(role.uuid, organizationUsers);
  const isDefault = DEFAULT_ROLE_NAMES.has(role.name);
  const { accounts, hasAccessToAllAccounts } = getAccountAccessForRole(
    role,
    allAccounts
  );
  const isAdmin = role.name === OrganizationUser.ROLE.ADMIN;


  const hasMoreThanFourAccounts = accounts.length > 4;
  const groupedAccounts = groupAccountsByOrganization(
    accounts.filter((acc): acc is API.Account => !("externalAccountTypeName" in acc)) as unknown as Account[],
    institution?.long_name
  ) as unknown as Map<string, API.Account[]>;
  const hasMultipleOrgs = groupedAccounts.size > 1;
  const permissions = getPermissionDescriptions(role.permissions);

  // Compute dynamic subtitle for "Make external transfer" permission
  const externalTransferSubtitle = getExternalAccountSubtitle(
    role,
    allAccounts
  );

  // Apply dynamic subtitle to "Make external transfer" permission if it exists
  const permissionsWithDynamicSubtitle = permissions.map((perm) =>
    perm.modelStr === MODEL_STR.ACH_TRANSFER && externalTransferSubtitle
      ? { ...perm, subtitleFtlId: externalTransferSubtitle }
      : perm
  );

  const limits: API.Limits.ByType = Object.keys(role.limits).length > 0 ? (role.limits as API.Limits.ByType) : orgLimits ?? {};

  // Check if role has write permission for at least one limit type
  const hasPermissionForAnyLimits = hasAnyWritePermissionForLimits(
    role.permissions,
    LIMITS_DATA
  );

  return (
    <ContentCard kind="bordered" paddingSize="xl">
      <div className={styles.roleInfo}>
        <Row gapSize="s">
          <Row.Item>
            <Row gapSize="xs" alignItems="center">
              <h2>{role.name}</h2>
              {isDefault && (
                <Chip
                  isLabelVisible={false}
                  startIcon="lock"
                  label={l10n.getString("org-role-locked")}
                />
              )}
            </Row>
            {role.description && (
              <p className={styles.roleDescription}>{role.description}</p>
            )}
          </Row.Item>
          {!isDefault && (
            <>
              <Row.Item shrink>
                <Button kind="secondary" size="xs" startIcon="copy" onClick={() => role.uuid && onNavigateToDuplicateRole(role.uuid)}>
                  {l10n.getString("org-role-button-duplicate")}
                </Button>
              </Row.Item>
              <Row.Item shrink>
                <Button kind="primary" size="xs" startIcon="edit" onClick={() => role.uuid && onNavigateToEditRole(role.uuid)}>
                  {l10n.getString("org-role-button-edit")}
                </Button>
              </Row.Item>
            </>
          )}
          <Row.Item shrink>
            <MenuButton>
              {!isDefault && (
              <MenuButton.Item
                label={l10n.getString("org-role-delete-role")}
                onSelect={() => setIsDeleteModalOpen(true)}
              />)}
              <MenuButton.Item
                label={l10n.getString("org-role-copy-role")}
                onSelect={async () => {
                  const url = `${window.location.origin}/permissions/roles/${role.uuid}`;
                  try {
                    await navigator.clipboard.writeText(url);
                    sendNotification({ type: "success", text: l10n.getString("org-role-copy-link-success") });
                  } catch (error) {
                    sendNotification({ type: "negative", text: l10n.getString("org-role-copy-link-error") });
                  }
                }}
              />
            </MenuButton>
          </Row.Item>
        </Row>
      </div>

      {usersForRole.length > 0 && (
        <>
          <hr className={styles.divider} />
          <div className={styles.usersSection}>
            <p
              className={`${styles.usersSectionHeader} fontSize--xs fontWeight--bold`}
            >
              {l10n.getString("org-role-assigned-to")}
            </p>
            {usersForRole.map((user) => (
              <div key={user.uuid} className="padding--bottom--m">
                <Row gapSize="s" alignItems="center">
                  <Tooltip text={user.user.email}>
                    <Avatar
                      label={user.fullName()}
                      kind="tonal"
                      initials={user.userInitials()}
                    ></Avatar>
                  </Tooltip>
                  {user.fullName()}
                </Row>
              </div>
            ))}
          </div>
        </>
      )}
      <hr className={styles.divider} />
      <div className={styles.usersSection}>
        <Row gapSize="xs" alignItems="center">
          <div
            className={`${styles.usersSectionHeader} fontSize--xs fontWeight--bold`}
          >
            {l10n.getString("org-role-accounts")}
          </div>
          <div style={{ transform: "translateY(-6px)" }}>
            <Chip
              count={String(accounts.length)}
              isLabelVisible={false}
              label=""
            />
          </div>
        </Row>

        {hasAccessToAllAccounts && (
          <div>
            <LongRoleTile
              iconClass="narmi-icon-bank"
              title={l10n.getString("org-role-account-all-accounts")}
              description={l10n.getString(
                "org-role-account-all-accounts-description"
              )}
            />
             {accounts.length > 0 && !expandAccountList && (
               <ToggleAccountsButton
                  isExpanded={expandAccountList}
                  onToggle={() => setExpandAccountList((prev) => !prev)}
                  collapsedLabel={l10n.getString("org-role-button-view-accounts")}
                  expandedLabel={l10n.getString("org-role-button-see-less")}
                />
             )}
             {expandAccountList && hasMultipleOrgs && (
               <OrgGroupedAccounts groupedAccounts={groupedAccounts} />
             )}
            {expandAccountList && !hasMultipleOrgs && accounts.length > 0 && (
              <div>
                <div
                  style={{ marginTop: "20px" }}
                  className={styles.accountsTileGrid}
                >
                  {accounts.map((account) => (
                    <AccountTile key={account.id} account={account} />
                  ))}
                 </div>
               <ToggleAccountsButton
                  isExpanded={expandAccountList}
                  onToggle={() => setExpandAccountList((prev) => !prev)}
                  collapsedLabel={l10n.getString("org-role-button-view-accounts")}
                  expandedLabel={l10n.getString("org-role-button-see-less")}
                />
               </div>
             )}
             {expandAccountList && hasMultipleOrgs && accounts.length > 0 && (
               <ToggleAccountsButton
                  isExpanded={expandAccountList}
                  onToggle={() => setExpandAccountList((prev) => !prev)}
                  collapsedLabel={l10n.getString("org-role-button-view-accounts")}
                  expandedLabel={l10n.getString("org-role-button-see-less")}
                />
             )}
          </div>
        )}

           {!hasAccessToAllAccounts && accounts.length > 0 && (
             <div>
               {hasMultipleOrgs && hasMoreThanFourAccounts && !expandAccountList ? (
                 <>
                   <OrgGroupedAccounts groupedAccounts={groupedAccounts} limit={4} />
                <ToggleAccountsButton
                  isExpanded={expandAccountList}
                  onToggle={() => setExpandAccountList((prev) => !prev)}
                  collapsedLabel={l10n.getString("org-role-button-view-accounts")}
                  expandedLabel={l10n.getString("org-role-button-see-less")}
                />
                 </>
               ) : (
                 <>
                   {hasMultipleOrgs && (
                     <OrgGroupedAccounts groupedAccounts={groupedAccounts} />
                   )}
                   {!hasMultipleOrgs && (
                     <AccountsGrid
                       accounts={
                         expandAccountList
                           ? accounts
                           : accounts.slice(0, 4)
                       }
                     />
                   )}
                    {hasMoreThanFourAccounts && (
                      <ToggleAccountsButton
                        isExpanded={expandAccountList}
                        onToggle={() => setExpandAccountList((prev) => !prev)}
                        collapsedLabel={l10n.getString("org-role-button-view-all")}
                        expandedLabel={l10n.getString("org-role-button-see-less")}
                      />
                    )}
                 </>
               )}
             </div>
           )}
      </div>
      <hr className={styles.divider} />
      <div className={styles.usersSection}>
        <Row gapSize="xs" alignItems="center">
          <Row.Item>
            <div
              className={`${styles.usersSectionHeader} fontSize--xs fontWeight--bold`}
            >
              {l10n.getString("org-role-permissions")}
            </div>
          </Row.Item>
          <Row.Item shrink>
            <div style={{ marginBottom: "var(--space-s)" }}>
              <Chip
                label={l10n.getString("org-role-same-across-accounts")}
                startIcon="check"
              />
            </div>
          </Row.Item>
        </Row>
        {isAdmin && (
          <>
            <LongRoleTile
              iconClass="narmi-icon-check"
              title={l10n.getString("org-role-admin-title")}
              description={l10n.getString("org-role-admin-description")}
            />
            {showAdminPermissions &&
              permissionsWithDynamicSubtitle.map((permission) => {
                const { translatedTitle, translatedSubtitle } =
                  translatePermissionFtlIds(permission, l10n);
                return (
                  <Row key={translatedTitle} className={styles.permissionsRow}>
                    <span
                      className={`narmi-icon-check ${styles.accountsIcon}`}
                      aria-hidden="true"
                    />
                    <div>
                      <p className={`fontSize--16px ${styles.permissionTitle}`}>
                        {translatedTitle}
                      </p>
                      <p
                        className={`fontSize--14px ${styles.permissionSubtitle}`}
                      >
                        {translatedSubtitle}
                      </p>
                    </div>
                  </Row>
                );
              })}
              <ToggleAccountsButton
                isExpanded={showAdminPermissions}
                onToggle={() => setShowAdminPermissions((prev) => !prev)}
                collapsedLabel={l10n.getString("org-role-button-view-permissions")}
                expandedLabel={l10n.getString("org-role-button-see-less")}
              />
          </>
        )}
        {role.name === OrganizationUser.ROLE.VIEWER && (
          <LongRoleTile
            iconClass="narmi-icon-eye"
            title={l10n.getString("org-role-viewer-title")}
            description={l10n.getString("org-role-viewer-description")}
          />
        )}
        {!isAdmin &&
          role.name !== OrganizationUser.ROLE.VIEWER && (
            <>
              {permissionsWithDynamicSubtitle
                .slice(0, 5)
                .map((permission, index) => {
                  const { translatedTitle, translatedSubtitle } =
                    translatePermissionFtlIds(permission, l10n);
                  return (
                    <Row key={index} className={styles.permissionsRow}>
                      <span
                        className={`narmi-icon-check ${styles.accountsIcon}`}
                        aria-hidden="true"
                      />
                      <div>
                        <p
                          className={`fontSize--16px ${styles.permissionTitle}`}
                        >
                          {translatedTitle}
                        </p>
                        <p
                          className={`fontSize--14px ${styles.permissionSubtitle}`}
                        >
                          {translatedSubtitle}
                        </p>
                      </div>
                    </Row>
                  );
                })}
              {showAllPermissions &&
                permissionsWithDynamicSubtitle
                  .slice(5)
                  .map((permission, index) => {
                    const { translatedTitle, translatedSubtitle } =
                      translatePermissionFtlIds(permission, l10n);
                    return (
                      <Row key={index + 5} className={styles.permissionsRow}>
                        <span
                          className={`narmi-icon-check ${styles.accountsIcon}`}
                          aria-hidden="true"
                        />
                        <div>
                          <p
                            className={`fontSize--16px ${styles.permissionTitle}`}
                          >
                            {translatedTitle}
                          </p>
                          <p
                            className={`fontSize--14px ${styles.permissionSubtitle}`}
                          >
                            {translatedSubtitle}
                          </p>
                        </div>
                      </Row>
                    );
                  })}
                {permissionsWithDynamicSubtitle.length > 5 && (
                  <ToggleAccountsButton
                    isExpanded={showAllPermissions}
                    onToggle={() => setShowAllPermissions((prev) => !prev)}
                    collapsedLabel={l10n.getString("org-role-button-view-all")}
                    expandedLabel={l10n.getString("org-role-button-see-less")}
                  />
                )}
            </>
          )}
      </div>
      
      {hasInstitutionLoaded && Object.keys(limits).length > 0 && hasPermissionForAnyLimits && (
        <>
        <hr className={styles.divider} />
        <div className={styles.usersSection}>
          <div
            className={`${styles.usersSectionHeader} fontSize--xs fontWeight--bold`}
          >
            {isAdmin ? l10n.getString("org-role-organization-limits") : l10n.getString("org-role-limits")}
          </div>
          {isAdmin && (<p className={"fontSize--s"} style={{ color: "var(--nds-color-gray-medium)" }}>{l10n.getString("org-role-organization-limits-subtitle")}</p>)}
           {Object.entries(limits).map(([limitType, limitValue]) => {
             const limitData = LIMITS_DATA[limitType as keyof typeof LIMITS_DATA];
             const hasAnyPositiveLimit = Object.values(limitValue).some(
               (v) => Number(v) > 0
             );
             if (limitData && hasAnyPositiveLimit && hasWritePermissionForModels(role.permissions, limitData.modelStrs)) {
              const sortedPeriods = Object.entries(limitValue)
                .sort(([periodA], [periodB]) => Number(periodA) - Number(periodB))
                .filter(([, amount]) => Number(amount) > 0);

              return (
                <div key={limitType}>
                  <Row>
                    <Row.Item>
                      <p>{l10n.getString(limitData.ftlId)}</p>
                    </Row.Item>
                    <Row.Item shrink>
                      <div style={{ textAlign: "right" }}>
                        {sortedPeriods.map(([period, amount]) => {
                          const periodLabel = l10n.getString("org-role-limit-period-per-days", { days: Number(period) });
                          return (
                            <p
                              key={period}
                              style={period !== "1" ? { color: "var(--color-mediumGrey)" } : undefined}
                            >
                              {formatNumber(Number(amount), "currency")} {periodLabel}
                            </p>
                          );
                        })}
                      </div>
                    </Row.Item>
                  </Row>
                  <hr className={styles.limitsdivider} />
                </div>
              );
            }
            return null;
           })}
         </div>
         </>
       )}
        <DeleteRoleModal
          role={role}
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          organizationUsers={organizationUsers}
          assignableRoles={assignableRoles}
          onRoleDeleted={refresh}
          onRefreshUsers={onRefreshUsers}
        />
     </ContentCard>
   );
 }

function OrganizationRolesContent({
  organizationUsers,
  allAccounts,
  onRefreshUsers,
}: {
  organizationUsers?: OrganizationUser[];
  allAccounts?: (API.Account | API.ExternalAccount)[];
  onRefreshUsers?: () => void;
}) {
  const { l10n } = useLocalization();
  const { isLoading, error, assignableRoles } = useRolesContext();
  const { sendNotification } = useNotificationContext();
  const navigate = useNavigate();
  const { roleUuid } = useParams<{ roleUuid?: string }>();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const institution = useContext(InstitutionSettingsContext);
  const orgLimits = institution?.features?.limits || {};
  const hasInstitutionLoaded = !!institution?.features?.limits;

  const selectedRole = assignableRoles.find((role) => role.uuid === roleUuid) || null;

  const navigateToRole = (selectedRoleUuid: string) => {
    navigate(`/roles/${selectedRoleUuid}`);
  };

  useEffect(() => {
    if (error) {
      sendNotification({
        type: "negative",
        text: `${l10n.getString("org-role-error-toast")}: ${error.message}`,
      });
    }
  }, [error, sendNotification]);

  // Auto-select first default role on initial load if none selected
  useEffect(() => {
    if (!roleUuid && assignableRoles.length > 0) {
      const { defaultRoles } = splitRoles(assignableRoles);

      if (defaultRoles.length > 0) {
        const firstDefaultRole = defaultRoles[0];
        if (firstDefaultRole.uuid) {
          navigate(`/roles/${firstDefaultRole.uuid}`, { replace: true });
        }
      }
    }
  }, [assignableRoles, navigate, roleUuid]);

  // Clear selection if the selected role is deleted
  useEffect(() => {
    if (roleUuid && !selectedRole && assignableRoles.length > 0) {
      navigate("/roles", { replace: true });
    }
  }, [assignableRoles.length, navigate, roleUuid, selectedRole]);

  if (isLoading) {
    return (
      <div className={styles.statusContainer}>
        {l10n.getString("org-role-loading")}
      </div>
    );
  }

  const { defaultRoles, customRoles } = splitRoles(assignableRoles);

  const filteredDefaultRoles = filterRolesByName(defaultRoles, searchQuery);
  const filteredCustomRoles = filterRolesByName(customRoles, searchQuery);

  return (
    <div className={styles.container}>
      {/* Mobile role selector dropdown */}
      <div className={styles.mobileRoleSelector}>
        <Select
          value={roleUuid || ""}
          onChange={(value: string) => {
            if (value) {
              navigateToRole(value);
            }
          }}
        >
           {defaultRoles.length > 0 && (
             <Select.Category
               isFlat
               label={<span className="fontSize--xs">{l10n.getString("org-role-section-default")}</span> as unknown as string}
             >
               {defaultRoles.map((role) => (
                 <Select.Item key={role.uuid} value={role.uuid || ""}>
                   {role.name}
                 </Select.Item>
               ))}
             </Select.Category>
           )}
           {customRoles.length > 0 && (
             <Select.Category
               isFlat
               label={<span className="fontSize--xs">{l10n.getString("org-role-section-custom")}</span> as unknown as string}
             >
               {customRoles.map((role) => (
                 <Select.Item key={role.uuid} value={role.uuid || ""}>
                   {role.name}
                 </Select.Item>
               ))}
             </Select.Category>
           )}
          <Select.Action
            onSelect={() => {
              navigate("/roles/new");
            }}
          >
            <Button
              kind="plain"
              endIcon="arrow-right"
              label={l10n.getString("org-role-create-new")}
            />
          </Select.Action>
        </Select>
      </div>

      <Row gapSize="m">
        <div className={styles.sidebar}>
          <ContentCard kind="bordered" paddingSize="none">
            <div className={styles.sidebarHeader}>
              <div className={styles.sidebarControls}>
                <div className={styles.searchInput}>
                  <TextInput
                    placeholder={l10n.getString("org-role-search-placeholder")}
                    startIcon={"search"}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setSearchQuery(event.target.value)
                    }
                    value={searchQuery}
                  />
                </div>
                <Button
                  startIcon="plus"
                  kind="primary"
                  size="m"
                  onClick={() => navigate("/roles/new")}
                >
                  {l10n.getString("org-role-button-create")}
                </Button>
              </div>
            </div>
            <div className={styles.rolesList}>
              {filteredDefaultRoles.length > 0 && (
                <div className={styles.roleSection}>
                  <p
                    className={`${styles.roleSectionHeader} fontSize--xs fontWeight--bold`}
                  >
                    {l10n.getString("org-role-section-default")}
                  </p>
                  <div className={styles.rolesContainer}>
                    {filteredDefaultRoles.map((role) => (
                      <RoleTile
                        key={role.uuid}
                        role={role}
                        selectedUuid={roleUuid}
                        organizationUsers={organizationUsers}
                        isDefault={true}
                        l10n={l10n}
                        onSelect={navigateToRole}
                      />
                    ))}
                  </div>
                </div>
              )}
              <div className={styles.roleSection}>
                <p
                  className={`${styles.roleSectionHeader} fontSize--xs fontWeight--bold`}
                >
                  {l10n.getString("org-role-section-custom")}
                </p>
                <div className={styles.rolesContainer}>
                  {filteredCustomRoles.length > 0 &&
                    filteredCustomRoles.map((role) => (
                      <RoleTile
                        key={role.uuid}
                        role={role}
                        selectedUuid={roleUuid}
                        organizationUsers={organizationUsers}
                        l10n={l10n}
                        onSelect={navigateToRole}
                      />
                    ))}
                  {customRoles.length === 0 && (
                    <div className={styles.emptyCustomRolesCard}>
                      <ContentCard kind="bordered">
                        <p className="fontColor--heading fontWeight--bold fontSize--m">
                          {l10n.getString("org-role-no-custom")}
                        </p>
                        <p className="fontSize--s">
                          {l10n.getString("org-role-tailor-access")}
                        </p>
                        <Button
                          endIcon="plus"
                          onClick={() => {
                            navigate("/roles/new");
                          }}
                        >
                          {l10n.getString("org-role-create-a-role")}
                        </Button>
                      </ContentCard>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </ContentCard>
        </div>
        <div className={styles.content}>
          <RoleInfo
            role={selectedRole}
            organizationUsers={organizationUsers}
            allAccounts={allAccounts}
            orgLimits={orgLimits}
            hasInstitutionLoaded={hasInstitutionLoaded}
            l10n={l10n}
            assignableRoles={assignableRoles}
            onRefreshUsers={onRefreshUsers}
            onNavigateToEditRole={(selectedRoleUuid) => {
              navigate(`/roles/${selectedRoleUuid}/edit`);
            }}
            onNavigateToDuplicateRole={(selectedRoleUuid) => {
              navigate(`/roles/${selectedRoleUuid}/duplicate`);
            }}
          />
        </div>
      </Row>
    </div>
  );
}

export default function OrganizationRoles({
  organizationUuid,
  organizationUsers,
  allAccounts,
  onRefreshUsers,
}: OrganizationRolesProps) {
  const { l10n } = useLocalization();
  if (!organizationUuid) {
    return (
      <div style={{ minHeight: "800px", padding: "32px" }}>
        {l10n.getString("org-role-error-no-org-id")}
      </div>
    );
  }

  return (
    <OrganizationRolesContent
      organizationUsers={organizationUsers}
      allAccounts={allAccounts}
      onRefreshUsers={onRefreshUsers}
    />
  );
}
